import { CGU } from '@components/consents/CGU';
import { EmailConsent } from '@components/consents/EmailConsent';
import { PhoneInput } from '@components/utils/inputElements/phoneInput';
import { FORM, ONBOARDING } from '@constants';
import styled from '@emotion/styled';
import {
    FormContext,
    FormShape,
    OutboundLinkContext,
    TrackEvent,
} from '@enums';
import {
    ComponentConsentsEmail,
    Enum_Componentformtemplatesregistration_Identifier,
    Enum_Forms_Type,
} from '@graphql/generated/graphql';
import { CustomerInputType } from '@hello-pimster/pimster-crm-sdk';
import { UseFormSubmitMutate } from '@hooks';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { UIVariant, Button, FormControl, Input, VStack } from '@lib/uikit';
import { TextVariant } from '@lib/uikit/styles/text';
import { getDefaultEmailConsent } from '@utils/consents';
import { isPhoneValid } from '@utils/phone';
import { Field, FieldProps, Form as FormikForm, Formik } from 'formik';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import * as yup from 'yup';

interface Props {
    formName: string;
    formUID: string;
    formType: Enum_Forms_Type;
    formContext?: FormContext;
    formShape: FormShape;
    customerInputType: CustomerInputType;
    companyName: string;
    ctaText?: string;
    emailConsent?: ComponentConsentsEmail;
    withCGU?: boolean;
    isDisabled?: boolean;
    isError?: boolean;
    isSubmitting: boolean;
    variant?: UIVariant;
    identifier: Enum_Componentformtemplatesregistration_Identifier;
    onSubmit: UseFormSubmitMutate;
}

interface FormData {
    optin: boolean;
    email?: string;
    phoneNumber?: string;
}

const Form = styled(FormikForm)`
    width: 100%;
`;

export const RegistrationForm: FC<Props> = ({
    formName,
    formUID,
    formType,
    formContext,
    formShape,
    customerInputType,
    companyName,
    ctaText,
    emailConsent,
    withCGU,
    isDisabled,
    isError,
    isSubmitting,
    variant = UIVariant.Outline,
    identifier,
    onSubmit,
}) => {
    const { t } = useTranslation([ONBOARDING, FORM]);
    const { track } = useAnalytics();
    const [formHasBeenFocused, setFormHasBeenFocused] =
        useState<boolean>(false);
    const isEmailIdentifier =
        identifier === Enum_Componentformtemplatesregistration_Identifier.Email;

    const buttonText = isError
        ? t('submission_error')
        : ctaText || t('save_product');

    const initialValues: FormData = {
        optin: getDefaultEmailConsent(emailConsent),
        email: isEmailIdentifier ? '' : undefined,
        phoneNumber: isEmailIdentifier ? undefined : '',
    };

    const validationSchema = () => {
        const optin = yup.boolean();

        if (emailConsent?.IsRequired) {
            optin.oneOf([true], t('errors.required'));
        }

        if (isEmailIdentifier) {
            return yup.object({
                optin,
                email: yup
                    .string()
                    .email(t(`${FORM}:errors.email.format`))
                    .required(t(`${FORM}:errors.required`)),
                phoneNumber: yup.string().nullable(),
            });
        } else {
            return yup.object({
                optin,
                phoneNumber: yup
                    .string()
                    .test(
                        'is-valid-phone',
                        t('errors.phone.format', { ns: FORM }),
                        async (value) => await isPhoneValid(value)
                    ),
                email: yup.string().nullable(),
            });
        }
    };

    const handleFocus = () => {
        if (!formHasBeenFocused) {
            track(TrackEvent.FormStarted, {
                name: formName,
                form_uid: formUID,
                type: formType,
                context: formContext,
                shape: formShape,
            });
        }

        setFormHasBeenFocused(true);
    };

    const handleSubmit = (data: FormData) => {
        onSubmit({
            customerInputType,
            data: {
                ...(isEmailIdentifier
                    ? { email: data.email }
                    : { phoneNumber: data.phoneNumber }),
                optIns: isEmailIdentifier
                    ? { email: true }
                    : { sms: true, whatsapp: true },
            },
        });
    };

    return (
        <Formik
            validateOnBlur
            validateOnMount={false}
            validateOnChange={false}
            initialValues={initialValues}
            validationSchema={validationSchema()}
            onSubmit={handleSubmit}
        >
            <Form onFocus={handleFocus}>
                <VStack spacing='xs'>
                    {isEmailIdentifier ? (
                        <Field type='email' name='email'>
                            {({ form, field }: FieldProps) => (
                                <FormControl
                                    name='email'
                                    errorMessage={form.errors.email as string}
                                    variant={variant}
                                >
                                    <Input
                                        {...field}
                                        type='email'
                                        placeholder={
                                            t(`${FORM}:labels.email`) + '*'
                                        }
                                        variant={variant}
                                    />
                                </FormControl>
                            )}
                        </Field>
                    ) : (
                        <Field>
                            {({ form }: FieldProps) => (
                                <PhoneInput
                                    name='phoneNumber'
                                    errorMessage={
                                        form.errors.phoneNumber as string
                                    }
                                    variant={variant}
                                />
                            )}
                        </Field>
                    )}
                    {emailConsent && (
                        <EmailConsent
                            companyName={companyName}
                            element={emailConsent}
                            outboundLinkContext={
                                OutboundLinkContext.RegistrationForm
                            }
                            textVariant={
                                variant === UIVariant.DarkTranslucent
                                    ? TextVariant.Dark
                                    : undefined
                            }
                        />
                    )}
                    <Button
                        isFullWidth
                        type='submit'
                        disabled={isError || isDisabled}
                        isLoading={isSubmitting}
                        colorScheme={
                            variant === UIVariant.DarkTranslucent
                                ? 'white'
                                : 'black'
                        }
                    >
                        {buttonText}
                    </Button>
                    {withCGU && (
                        <CGU
                            textVariant={
                                variant === UIVariant.DarkTranslucent
                                    ? TextVariant.Dark
                                    : undefined
                            }
                            outboundLinkContext={
                                OutboundLinkContext.RegistrationForm
                            }
                        />
                    )}
                </VStack>
            </Form>
        </Formik>
    );
};
